import { Link } from 'react-router';
import { Badge } from '@/components/ui/badge';
import { H3, P } from '@/components/ui/typography';
import { themeColorLinkClassNames } from '@/components/ui/utils';
import { cn } from '@/lib/utils';
import type { SpotlightCardModel } from '@/components/Spotlight/spotlight';
import 'material-symbols';

const CARD_SURFACE = cn(
    'flex h-full w-full flex-col items-center justify-center gap-3 rounded-2xl border px-6 pt-6 pb-6 text-center shadow-sm',
    'light:bg-gz-surface-light light:border-gz-neutral-200',
    'dark:bg-gz-blue-700 dark:border-gz-blue-500',
);

const CARD_HOVER = cn(
    'transition-[background-color,transform] duration-200 ease-out',
    'motion-safe:hover:-translate-y-px',
    'light:hover:bg-gz-tan-250',
    'dark:hover:bg-gz-blue-550',
);

const LABEL_BADGE = cn(
    'rounded-full border px-2 py-0.5 type-caption font-normal',
    'bg-gz-gold-100 border-gz-gold-700 text-gz-neutral-900',
    'dark:bg-gz-blue-800 dark:border-gz-blue-600 dark:text-gz-blue-50',
);

export interface SpotlightCardProps {
    card: SpotlightCardModel;
}

const isAppPath = (url: string) => url.startsWith('/') && !url.startsWith('//');

const spotlightTrackingProps = (card: SpotlightCardModel) => ({
    'data-track-click': 'Spotlight Card Clicked',
    'data-track-spotlight-slug': card.slug,
    'data-track-url': card.url,
    'data-track-title': card.title ?? '',
    'data-track-external': String(card.external),
});

export const SpotlightCard = ({ card }: SpotlightCardProps) => {
    const body = (
        <>
            {card.image && card.imageLayout !== 'none' && (
                <div
                    className={cn(
                        'flex items-center justify-center',
                        card.imageLayout === 'replaces-title' ? 'w-full' : 'max-w-[12rem]',
                        'dark:rounded-md dark:bg-white dark:px-3 dark:py-2',
                    )}
                >
                    <img
                        src={card.image.url}
                        alt={card.imageLayout === 'replaces-title'
                            ? (card.image.alternativeText || card.title || 'Spotlight')
                            : ''}
                        className={cn(
                            'object-contain',
                            card.imageLayout === 'replaces-title' ? 'max-h-28 w-full' : 'max-h-10 w-auto',
                        )}
                    />
                </div>
            )}
            {card.imageLayout === 'none' && card.label && (
                <Badge className={LABEL_BADGE}>{card.label}</Badge>
            )}
            {((card.title && card.imageLayout !== 'replaces-title') || card.description) && (
                <div className="flex flex-col items-center gap-1">
                    {card.title && card.imageLayout !== 'replaces-title' && (
                        <H3 variant="type-body-large-bold" className="text-gz-neutral-900 dark:text-gz-neutral-100">
                            {card.title}
                        </H3>
                    )}
                    {card.description && (
                        <P variant="type-caption" className="light:text-gz-neutral-900 dark:text-gz-neutral-200">
                            {card.description}
                        </P>
                    )}
                </div>
            )}
            {card.clickable && card.caption && (
                <span className={cn(
                    'inline-flex items-center gap-1 border-b border-current pb-px type-caption font-normal',
                    themeColorLinkClassNames,
                )}
                >
                    {card.caption}
                    <span className="material-symbols-rounded text-base leading-none" aria-hidden>
                        {card.external && !card.captionIsFallback ? 'open_in_new' : 'arrow_forward'}
                    </span>
                </span>
            )}
        </>
    );

    if (!card.clickable || !card.url) {
        return <article className={CARD_SURFACE}>{body}</article>;
    }

    const linkClass = cn(
        CARD_SURFACE,
        CARD_HOVER,
        'focus-visible:outline-2 focus-visible:outline-offset-2',
        'light:focus-visible:outline-gz-marine-700 dark:focus-visible:outline-white',
    );

    if (card.external) {
        return (
            <a
                href={card.url}
                target="_blank"
                rel="noopener noreferrer"
                className={linkClass}
                {...spotlightTrackingProps(card)}
            >
                {body}
            </a>
        );
    }

    if (isAppPath(card.url)) {
        return (
            <Link to={card.url} className={linkClass} {...spotlightTrackingProps(card)}>
                {body}
            </Link>
        );
    }

    return (
        <a href={card.url} className={linkClass} {...spotlightTrackingProps(card)}>
            {body}
        </a>
    );
};
